import {
  CheckCircleOutlined,
  CheckOutlined,
  EditOutlined,
  FileDoneOutlined,
  StarOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import { Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

/**
 * What EZ-Sign is for, above the figures. The sheet on the right is decoration
 * — it names no request and no status — and goes on a narrow screen, where the
 * copy needs the width.
 */
export function WelcomeBanner() {
  const { t } = useTranslation()
  const benefits = [
    {
      icon: <UnorderedListOutlined aria-hidden />,
      text: t("esign.welcome.guided")
    },
    { icon: <EditOutlined aria-hidden />, text: t("esign.welcome.anyDevice") },
    {
      icon: <CheckCircleOutlined aria-hidden />,
      text: t("esign.welcome.tracking")
    }
  ]

  return (
    <section
      aria-label={t("esign.welcome.label")}
      className={
        "grid grid-cols-1 items-center gap-6 rounded-xl border border-solid border-[#D5EAED] bg-[#EEF7F8] px-6 py-5 md:grid-cols-[minmax(0,1fr)_15rem]"
      }
    >
      <div className={"min-w-0"}>
        <div
          className={
            "mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] text-primary-strong"
          }
        >
          <StarOutlined aria-hidden />
          {t("esign.welcome.eyebrow")}
        </div>
        <h2
          className={
            "m-0 !text-left text-[22px] font-bold leading-snug text-slate-900"
          }
        >
          {t("esign.welcome.headline")}
          <br />
          <span className={"text-primary-strong"}>
            {t("esign.welcome.headlineAccent")}
          </span>
        </h2>
        <Text className={"mt-2 block max-w-xl text-slate-600"}>
          {t("esign.welcome.body")}
        </Text>
        <ul className={"m-0 mt-3 flex list-none flex-wrap gap-x-5 gap-y-2 p-0"}>
          {benefits.map((benefit) => (
            <li
              key={benefit.text}
              className={"flex items-center gap-1.5 text-[13px] text-slate-700"}
            >
              <span className={"text-primary-strong"}>{benefit.icon}</span>
              {benefit.text}
            </li>
          ))}
        </ul>
      </div>
      <div aria-hidden className={"relative hidden md:block"}>
        <div
          className={
            "rotate-[-2deg] rounded-lg border border-solid border-slate-200 bg-white p-3 shadow-card"
          }
        >
          <div
            className={
              "flex items-center gap-2 text-xs font-semibold text-slate-800"
            }
          >
            <FileDoneOutlined className={"text-primary-strong"} />
            {t("esign.welcome.sheetTitle")}
          </div>
          <div className={"mt-3 h-1.5 w-full rounded bg-slate-100"} />
          <div className={"mt-1.5 h-1.5 w-3/4 rounded bg-slate-100"} />
          <div
            className={
              "mt-4 flex items-center justify-between rounded border border-dashed border-[#9CCFD6] bg-[#EEF7F8] px-2 py-1.5 text-[11px] text-primary-strong"
            }
          >
            {t("esign.welcome.sheetSign")}
            <EditOutlined />
          </div>
        </div>
        <div
          className={
            "relative -mt-2 ml-4 flex items-center justify-center gap-1.5 rounded-md border border-solid border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700"
          }
        >
          <CheckOutlined className={"text-primary-strong"} />
          {t("esign.welcome.sheetTag")}
        </div>
      </div>
    </section>
  )
}
